<template>
  <div id="preview">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
    ></overlay-loading-progress>
    <v-card elevation="8" class="rounded-xl">
      <!-- Header Section with Gradient -->
      <v-card-title
        class="page-header-gradient white--text d-flex align-center pa-5"
      >
        <v-icon left large color="white" class="mr-3">mdi-pencil-box</v-icon>
        <h2 class="text-h5 font-weight-bold">{{ pageTitle }}</h2>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closePrompt" large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <!-- Loading Indicator -->
        <div class="d-flex justify-center mb-3" v-if="LoadingFlag">
          <b-spinner
            variant="primary"
            type="grow"
            label="Loading..."
          ></b-spinner>
        </div>
        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="confirmAlert"
        >
          <!-- Required Field Note -->
          <v-row>
            <v-col cols="12">
              <v-alert type="info" dense text outlined class="mb-4">
                <small
                  ><span class="text-danger">*</span> indicates required
                  field</small
                >
              </v-alert>
            </v-col>
          </v-row>

          <v-container class="py-0">
            <!-- Year, Contribution Type, Member Type Row -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Year
                </h6>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="JciYearCodeOptions"
                  :rules="JciYearCodeRules"
                  :loading="JciYearCodeOptionsLoading"
                  v-model="JciYearCode"
                  placeholder="Select Year"
                  dense
                  clearable
                  required
                  outlined
                  prepend-inner-icon="mdi-calendar"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Contribution Type
                </h6>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="ContributionTypeIdOptions"
                  :rules="ContributionTypeIdRules"
                  :loading="ContributionTypeIdOptionsLoading"
                  v-model="ContributionTypeId"
                  placeholder="Select Contribution Type"
                  dense
                  clearable
                  required
                  outlined
                  prepend-inner-icon="mdi-folder-multiple"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">Member Type</h6>
                <v-card class="pa-3 elevation-1">
                  <p class="mb-0 font-weight-bold">{{ MemberTypeName }}</p>
                </v-card>
              </v-col>
            </v-row>

            <!-- Start Date, End Date, Voting Permission -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Start Date
                </h6>
                <v-menu
                  v-model="menu1"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="StartDate"
                      placeholder="Select Start Date"
                      readonly
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      required
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="StartDate"
                    @input="menu1 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> End Date
                </h6>
                <v-menu
                  v-model="menu2"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="EndDate"
                      placeholder="Select End Date"
                      readonly
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      required
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="EndDate"
                    @input="menu2 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Voting Permission
                </h6>
                <v-switch
                  v-model="VotingFlag"
                  :label="`${VotingFlag ? 'Yes' : 'No'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
            </v-row>

            <!-- Amount, Tax Percentage, Final Amount -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Amount
                </h6>
                <v-text-field
                  v-model="Amount"
                  :rules="AmountRules"
                  placeholder="Enter Amount"
                  type="number"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-currency-inr"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Tax Percentage
                </h6>
                <v-text-field
                  v-model="TaxPercentage"
                  :rules="TaxPercentageRules"
                  placeholder="Enter Tax Percentage"
                  type="number"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-percent"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Final Amount
                </h6>
                <v-card class="pa-3 elevation-1">
                  <p class="mb-0">
                    <v-icon small class="mr-2">mdi-calculator</v-icon>
                    {{ FinalAmount }}
                  </p>
                </v-card>
              </v-col>
            </v-row>

            <!-- Welfare Amount, No. of Times, Fine -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Welfare Amount
                </h6>
                <v-text-field
                  v-model="WelfareAmount"
                  :rules="WelfareAmountRules"
                  placeholder="Enter Welfare Amount"
                  type="number"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-hand-heart"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> No. of Times
                </h6>
                <v-text-field
                  v-model="NoOfTimes"
                  :rules="NoOfTimesRules"
                  placeholder="Enter Number of Times"
                  type="number"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-counter"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Fine
                </h6>
                <v-switch
                  v-model="FineFlag"
                  :label="`${FineFlag ? 'Yes' : 'No'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Balance Amount
                </h6>
                <v-text-field
                  v-model="BalanceAmount"
                  :rules="BalanceAmountRules"
                  placeholder="Enter Balance Amount"
                  type="number"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-hand-heart"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Fine Amount and Fine End Date (Conditional) -->
            <v-row v-if="FineFlag">
              <v-col cols="12" sm="6" md="6">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Fine Amount
                </h6>
                <v-text-field
                  v-model="FineAmount"
                  :rules="FineAmountRules"
                  placeholder="Enter Fine Amount"
                  type="text"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-currency-inr"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Fine End Date
                </h6>
                <v-menu
                  v-model="menu3"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="FineEndDate"
                      placeholder="Select Fine End Date"
                      readonly
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar-alert"
                      v-bind="attrs"
                      v-on="on"
                      required
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="FineEndDate"
                    @input="menu3 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <!-- Deadline Flag, Payment Flag, Active Status -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Is Payment Allowed After
                  Fine Deadline?
                </h6>
                <v-switch
                  v-model="DeadlineFlag"
                  :label="`${DeadlineFlag ? 'Yes' : 'No'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Advance Payment
                </h6>
                <v-switch
                  v-model="PaymentFlag"
                  :label="`${PaymentFlag ? 'Yes' : 'No'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Active Status
                </h6>
                <v-switch
                  v-model="ActiveStatus"
                  :label="`${ActiveStatus ? 'Active' : 'Inactive'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
            </v-row>
          </v-container>

          <!-- Action Buttons -->
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                :disabled="!valid1"
                @click.prevent="confirmAlert"
                :loading="SubmitFlag"
                elevation="12"
                rounded
                large
                color="#1db954"
                class="font-size-h6 px-8 mr-3 white--text"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Save
              </v-btn>
              <v-btn
                @click="closePrompt"
                elevation="12"
                rounded
                large
                color="#757575"
                class="font-size-h6 px-8 white--text"
              >
                <v-icon left>mdi-close-circle</v-icon>
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    editRecordPrompt: {
      type: Boolean,
      required: true,
    },
    pageTitle: {
      type: String,
    },
    pageDescription: {
      type: String,
    },
    recordIndex: {
      required: true,
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      OverlayLoadingFlag: false,

      rows: {},

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCode: "",
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      ContributionTypeIdRules: [(v) => !!v || "Contribution Type is required"],
      ContributionTypeId: "",
      ContributionTypeIdOptions: [],
      ContributionTypeIdOptionsLoading: false,

      MemberTypeName: "",

      VotingFlag: 0,
      VotingFlagRules: [(v) => !!v || "Voting Permission is required"],

      StartDate: "",
      menu1: false,

      EndDate: "",
      menu2: false,
      Amount: "",
      AmountRules: [(v) => !!v || "Amount is required"],

      WelfareAmount: "",
      WelfareAmountRules: [
        // (v) => !!v || "Amount is required"
      ],

      BalanceAmount: "",
      BalanceAmountRules: [(v) => !!v || "Balance Amount is required"],

      TaxPercentage: "",
      TaxPercentageRules: [
        // (v) => !!v || "Tax Percentage is required"
      ],

      FinalAmount: 0,

      FineFlag: 0,
      FineFlagRules: [(v) => !!v || "Fine is required"],

      DeadlineFlag: 0,
      DeadlineFlagRules: [(v) => !!v || "Is allow deadline is required"],

      PaymentFlag: 0,
      PaymentFlagRules: [(v) => !!v || "Is allow payment is required"],

      ActiveStatus: 0,
      ActiveStatusRules: [(v) => !!v || "Active Status is required"],

      FineAmount: "",
      FineAmountRules: [
        // (v) => !!v || "Fine Amount is required"
      ],

      FineEndDate: "",
      menu3: false,

      NoOfTimes: "",
      NoOfTimesRules: [
        // (v) => (v && v <= 0) || "No of times should be above or equal to 0",
      ],
    };
  },
  computed: {},
  watch: {
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.JciYearCodeOptionsLoading = false;
    },
    ContributionTypeIdOptions: function () {
      console.log("watch ContributionTypeIdOptions");
      this.ContributionTypeIdOptionsLoading = false;
      this.getMemberType();
    },
    ContributionTypeId: function () {
      console.log("watch ContributionTypeId");
      this.getMemberType();
    },
    Amount: function () {
      console.log("watch Amount changed");
      this.calculateTotal();
    },
    TaxPercentage: function () {
      console.log("watch TaxPercentage changed");
      this.calculateTotal();
    },
    rows: {
      handler: function (newVal) {
        console.log("watch rows handler");
        var tr = newVal;
        var n1 = Object.keys(tr).length;
        console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
        if (n1 > 0) {
          this.JciYearCode = tr.YearId;
          this.ContributionTypeId = tr.ContributionTypeId;
          this.StartDate = tr.StartDate;
          this.EndDate = tr.EndDate;
          this.Amount = tr.ContributionAmount;
          this.WelfareAmount = tr.WelfareAmount;
          this.TaxPercentage = tr.Tax;
          this.NoOfTimes = tr.ContributionCount;
          this.VotingFlag = tr.VotingFlag;
          this.FineFlag = tr.FineFlag;
          this.FineAmount = tr.FineAmount;
          this.FineEndDate = tr.FineEndDate;
          this.DeadlineFlag = tr.DeadlineFlag;
          this.PaymentFlag = tr.PaymentFlag;
          this.ActiveStatus = tr.ActiveStatus;
          this.BalanceAmount = tr.BalanceAmount;
          this.getMemberType();
          // Call calculateTotal with a delay to ensure data is set
          this.$nextTick(() => {
            this.calculateTotal();
          });
        }
      },
      deep: true,
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    validateForm() {
      console.log("validateForm called");
      this.$refs.form1.validate();
    },
    resetForm() {
      console.log("resetForm called");
      this.$refs.form1.reset();
      //   this.$refs.ProfilePicture.reset();
    },
    resetValidation() {
      console.log("resetValidation called");
      this.$refs.form1.resetValidation();
    },
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideEditRecordPrompt", this.ResultFlag);
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var recordIndex = this.recordIndex;
      console.log("recordIndex=" + recordIndex);
      this.getRecordDetails();
      this.getJciYearCodeOptions();
      this.getContributionTypeIdOptions();
      this.LoadingFlag = false;
    },
    getJciYearCodeOptions() {
      this.LoadingFlag = true;
      this.JciYearCodeOptionsLoading = true;
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getContributionTypeIdOptions() {
      this.LoadingFlag = true;
      this.ContributionTypeIdOptionsLoading = true;
      console.log("ContributionTypeIdOptions called");
      var selectbox1_source = "ContributionTypeId";
      var selectbox1_destination = "ContributionTypeIdOptions";
      var selectbox1_url = "api/contribution-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    calculateTotal() {
      console.log("calculateTotal called");
      var Amount = this.Amount;
      var TaxPercentage = this.TaxPercentage;
      Amount = !isNaN(Amount) ? (Amount >= 0 ? Amount : 0) : 0;
      TaxPercentage =
        !isNaN(TaxPercentage) && TaxPercentage >= 0 ? TaxPercentage : 0;
      console.log("Amount=" + Amount + ", TaxPercentage=" + TaxPercentage);
      var TaxAmount = parseFloat(Amount) * (parseFloat(TaxPercentage) / 100);
      console.log("TaxAmount=" + TaxAmount);
      var TotalAmount = parseFloat(Amount) + parseFloat(TaxAmount);
      console.log("TotalAmount=" + TotalAmount);
      this.FinalAmount = isNaN(TotalAmount) ? 0 : TotalAmount;
    },
    getMemberType() {
      console.log("getMemberType called");
      var searchValue1 = this.ContributionTypeId;
      var index1 = this.ContributionTypeIdOptions.map((e) => e.value).indexOf(
        searchValue1
      );
      console.log("searchValue1=" + searchValue1 + ", index1=" + index1);
      var MemberTypeName =
        this.ContributionTypeIdOptions[index1].MemberTypeName;
      console.log("MemberTypeName=" + MemberTypeName);
      this.MemberTypeName = MemberTypeName;
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        this.toast("error", "Kindly fill the required fields");
      }
    },
    getRecordDetails() {
      // add todo in store and clear all fields in dialog
      console.log("getRecordDetails called");

      //   this.resetMessageTxt();

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      console.log("server_url=" + server_url + ", token=" + token);

      var recordIndex = this.recordIndex;

      console.log("recordIndex=" + recordIndex);

      if (recordIndex != "" && recordIndex != (null || undefined)) {
        var upload = {
          UserInterface: 1,
          YearwiseContributionId: recordIndex,
        };
        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var flag = 0;
        var records = [];

        // thisIns.pageLoadingOn()
        thisIns.OverlayLoadingFlag = true;
        thisIns.ProgessStart = 1;
        thisIns.valid = false;

        var CancelToken = this.CancelToken;
        var cancel;

        var add_url = server_url + "api/year-wise-contribution/show";

        console.log("add_url=" + add_url);

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
          cancelToken: new CancelToken(function executor(c) {
            // An executor function receives a cancel function as a parameter
            cancel = c;
          }),
        })
          .then(function (response) {
            // thisIns.pageLoadingOff()
            thisIns.OverlayLoadingFlag = false;
            thisIns.ProgessStart = 0;
            thisIns.valid = true;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.rows = records;
              this.toast("success", output);
            } else {
              this.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            thisIns.OverlayLoadingFlag = false;
            thisIns.ProgessStart = 0;
            thisIns.valid = true;
            console.log({ error });
          });
      } else {
        console.log("some errors occured");
        var message = "Year wise contribution id should not be empty";
        this.toast("error", message);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      //   validate1 = false;

      if (validate1) {
        this.SubmitFlag = true;
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-contribution/update";
        var upload = {
          YearwiseContributionId: this.recordIndex,
          YearId: this.JciYearCode,
          ContributionTypeId: this.ContributionTypeId,
          StartDate: this.StartDate,
          EndDate: this.EndDate,
          Amount: this.Amount,
          WelfareAmount: this.WelfareAmount,
          TaxPercentage: this.TaxPercentage,
          FineFlag: this.FineFlag,
          DeadlineFlag: this.DeadlineFlag,
          PaymentFlag: this.PaymentFlag,
          FineAmount: this.FineAmount,
          FineEndDate: this.FineEndDate,
          VotingFlag: this.VotingFlag,
          NoOfTimes: this.NoOfTimes,
          ActiveStatus: this.ActiveStatus,
          BalanceAmount: this.BalanceAmount,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.ResultFlag = true;
              thisIns.sweetAlert("success", output, true);
              thisIns.resetForm();
              thisIns.closePrompt();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        thisIns.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.page-header-gradient {
  background: linear-gradient(90deg, #004e92, #0a79df);
  border-radius: 10px 10px 0 0;
}

.v-card {
  max-height: 85vh;
  overflow-y: auto;
}

/* Custom scrollbar styling */
.v-card::-webkit-scrollbar {
  width: 8px;
}

.v-card::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.v-card::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.v-card::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
